/**
 * Hook para gerenciar preferências do módulo de Clientes (CRM).
 * Persiste no localStorage — mesmo padrão de `useTicketsPreferences` /
 * `useProjectsPreferences`.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'customers_preferences';

export type CustomersViewMode = 'grid' | 'table';

interface CustomersPreferences {
  viewMode: CustomersViewMode;
}

const DEFAULT_PREFERENCES: CustomersPreferences = {
  viewMode: 'grid',
};

export function useCustomersPreferences() {
  const [preferences, setPreferences] = useState<CustomersPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
      }
    } catch (e) {
      console.error('Error loading customers preferences:', e);
    }
    return DEFAULT_PREFERENCES;
  });

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (e) {
      console.error('Error saving customers preferences:', e);
    }
  }, [preferences]);

  const setViewMode = useCallback((mode: CustomersViewMode) => {
    setPreferences((prev) => ({ ...prev, viewMode: mode }));
  }, []);

  return {
    viewMode: preferences.viewMode,
    setViewMode,
  };
}
